/**
 * PipelineStageConfigPage — System Admin stage label/order configuration.
 * Blueprint: F-10 — new admin UI, System Admin access only.
 *
 * Admins can: rename stage labels, reorder stages (drag-to-reorder).
 * Admins cannot: delete stages with active_claimant_count > 0.
 * Immutable: stage_key (system key stays constant regardless of label edits).
 *
 * TODO: GET /admin/pipeline-stages/:tort_code — fetch current config.
 * TODO: PUT /admin/pipeline-stages/:tort_code — save reordered + renamed stages.
 * TODO: DELETE /admin/pipeline-stages/:stage_id — blocked server-side if active claimants.
 * TODO: Drag-to-reorder UX using @dnd-kit/sortable.
 */
import React, { useEffect, useState } from 'react';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { Save, Lock } from 'lucide-react';
import { usePipelineStages } from '@/hooks/cmm/usePipelineStages';
import type { TortCode, PipelineStageConfig } from '@/types/cmm/claimant.types';

const BREADCRUMBS = [{ label: 'Admin' }, { label: 'Pipeline Stage Config' }];

const PipelineStageConfigPage: React.FC = () => {
  const [activeTort, setActiveTort]  = useState<TortCode>('RLP');
  const { stages, fetchStages, updateStage } = usePipelineStages();

  useEffect(() => { fetchStages(activeTort); }, [activeTort]);

  const handleLabelChange = (stageId: string, newLabel: string) => {
    // TODO: optimistic UI update + PUT /admin/pipeline-stages on save
    console.warn('[PipelineStageConfigPage] label change — TODO:', stageId, newLabel);
  };

  return (
    <BaseLayout title="Pipeline Stage Config" subtitle="Rename and reorder pipeline stages per tort"
      breadcrumbs={BREADCRUMBS}>
      <div className="space-y-4">
        <div className="flex gap-3">
          {(['SMA','VGA','RLP','PFAS'] as TortCode[]).map(t => (
            <button key={t} onClick={() => setActiveTort(t)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                activeTort === t ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700'
              }`}>{t}</button>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-900">Stages — {activeTort}</h3>
            <button className="flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 rounded-md px-3 py-1.5">
              <Save className="w-4 h-4" /> Save Order
            </button>
          </div>

          {stages.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              Loading stages... (TODO: populate from GET /admin/pipeline-stages/{activeTort})
            </div>
          ) : stages.map((stage, idx) => (
            <div key={stage.id}
              className="flex items-center gap-4 px-5 py-3 border-b border-gray-100 hover:bg-gray-50">
              <span className="text-xs text-gray-400 w-5 text-center">{idx + 1}</span>
              <div className="flex-1">
                <input
                  className="text-sm font-medium text-gray-800 border-0 bg-transparent focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 w-full"
                  defaultValue={stage.stageLabel}
                  onBlur={e => handleLabelChange(stage.id, e.target.value)}
                />
                <p className="text-xs text-gray-400 px-2">key: <code>{stage.stageKey}</code></p>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {stage.allowsPacketGeneration && <span className="text-green-600">Packet ✓</span>}
                {stage.isTerminal            && <span className="text-gray-400">Terminal</span>}
                <span>{stage.activeClaimantCount} active</span>
                {stage.activeClaimantCount > 0
                  ? <Lock className="w-3.5 h-3.5 text-gray-300" aria-label="Cannot delete — has active claimants" />
                  : <button className="text-red-400 hover:text-red-600 text-xs">Delete</button>
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    </BaseLayout>
  );
};

export default PipelineStageConfigPage;
